import React, { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Button, Flex } from '@rover/kibble/core';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';
import { HeadingVariant } from '@rover/react-lib/src/components/lists/Carousel/components/Header';
import SearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/SearchResultsCarousel';
import {
  PageSectionId,
  useSectionRef,
} from '@rover/react-lib/src/pages/landing/SeoLocation/hooks/useSectionRef';
import {
  CarouselPaginateAction,
  ClickSitterCard,
  SearchResultLayoutType,
  SearchResultsCtaClick,
  SitterCardType,
  ViewSitterCard,
} from '@rover/react-lib/src/pages/landing/utilities/analyticsEvents';
import getMemberProfileUrl from '@rover/react-lib/src/pages/landing/utilities/getMemberProfileUrl';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResponse';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';
import { getPriceTransparencyLevel } from '@rover/utilities/priceTransparency';

type Props = {
  searchResponse: OptimizedSearchResponse;
  locale: Locale;
  heading: string;
  headingVariant: HeadingVariant;
  headingSize?: ThemeHeadingSizes;
  searchFilters: Partial<SearchFilters>;
  serviceTypeSlug: ServiceTypeSlugEnum;
  countryConfig: Pick<
    CountryConfiguration,
    | 'urls'
    | 'currencyCode'
    | 'hideBackgroundCheckVerbiage'
    | 'formattedVetCareReimbursementAmount'
    | 'bookingFeePercentage'
    | 'bookingFeeCap'
  >;
};

const LandingSearchResultsCarousel: React.FC<Props> = ({
  searchResponse,
  locale,
  heading,
  headingVariant,
  headingSize,
  searchFilters,
  serviceTypeSlug,
  countryConfig,
}) => {
  const priceTransparencyLevel = getPriceTransparencyLevel(searchResponse);
  const searchResults = useMemo(
    () =>
      searchResponse.results.map((searchResult) => ({
        ...searchResult,
        webUrl: getMemberProfileUrl(searchResult.webUrl, searchFilters, serviceTypeSlug, locale),
      })),
    [searchResponse.results, searchFilters, locale, serviceTypeSlug]
  );
  const preferCatReviews = !!searchResponse.derivedData?.shouldPreferCatReviews;
  const sectionRef = useSectionRef(PageSectionId.CarouselSearchResults, { threshold: 0.6 });

  return (
    <Flex htmlRef={sectionRef} gap="6x" flexDirection="column" alignItems="center">
      <SearchResultsCarousel
        searchResults={searchResults}
        heading={heading}
        headingVariant={headingVariant}
        headingSize={headingSize}
        // OptimizedSearchResponse.query subtypes are loosely defined and without explicitly casting this type, it assumes petType is unknown
        serviceType={searchResponse.query?.serviceType as ServiceType}
        priceTransparencyLevel={priceTransparencyLevel}
        locale={locale}
        preferCatReviews={preferCatReviews}
        onPaginate={({ pageStart, pageEnd, scrollSource }) => {
          emitAnalyticsEvent(
            new CarouselPaginateAction({
              serviceTypeSlug,
              pageStart,
              pageEnd,
              scrollSource,
            })
          );
        }}
        onSearchResultCardClick={() => {
          storeCrossPageAnalyticsEvent(
            new ClickSitterCard({
              serviceTypeSlug,
              cardType: SitterCardType.CAROUSEL,
            })
          );
        }}
        onSearchResultCardViewed={(personOpk) => {
          emitAnalyticsEvent(
            new ViewSitterCard({
              serviceTypeSlug,
              cardType: SitterCardType.CAROUSEL,
              sitterResultPersonOpk: personOpk,
            })
          );
        }}
        rootCarouselRef={sectionRef}
        countryConfig={countryConfig}
        petType={serviceTypeSlug === ServiceTypeSlugEnum['cat-sitting'] ? PetType.CAT : PetType.DOG}
      />
      <Button
        width="100%"
        maxWidth={['335px', '390px']}
        href={getSearchUrl(searchFilters, locale)}
        onPress={() => {
          storeCrossPageAnalyticsEvent(
            new SearchResultsCtaClick({
              serviceTypeSlug,
              layoutType: SearchResultLayoutType.CAROUSEL,
            })
          );
        }}
      >
        <Trans>View more</Trans>
      </Button>
    </Flex>
  );
};
export default LandingSearchResultsCarousel;
